import { createBaseApi } from '../baseApi';
import { API_ENDPOINTS } from '../../../utils/config';

// Helper to generate a smaller Cloudinary URL for thumbnails
const getThumbnailUrl = (url) => {
  if (!url) return url;
  // Check if it already has transformations or is not cloudinary
  if (!url.includes('res.cloudinary.com') || !url.includes('/upload/') || url.includes('/upload/c_')) {
    return url;
  }
  // Inject cloudinary transformation for width 300, format auto, quality auto
  return url.replace('/upload/', '/upload/c_limit,w_300,f_auto,q_auto/');
};


// Create base VTO result API
const vtoResultApi = createBaseApi('vtoResultApi');

// Enhanced API with VTO result endpoints
export const enhancedVTOResultApi = vtoResultApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get user's VTO results with filtering and pagination
    getUserVTOResults: builder.query({
      query: ({
        limit = 20,
        start = 0,
        garmentType,
        favorites,
        sort = 'created_at:desc'
      } = {}) => ({
        url: API_ENDPOINTS.VTO_RESULT.BASE,
        method: 'GET',
        params: {
          _limit: limit,
          _start: start,
          garmentType,
          favorites,
          _sort: sort
        }
      }),
      providesTags: (result) =>
        result?.results
          ? [
            'VTOResult',
            ...result.results.map(({ id }) => ({ type: 'VTOResult', id }))
          ]
          : ['VTOResult'],
      transformResponse: (response) => {
        // Handle error response from backend
        if (response?.error) {
          console.error('VTO Results API Error:', response.error);
          return { results: [], pagination: { total: 0, hasMore: false } };
        }

        if (response?.results) {
          console.log('[VTO API] Raw VTO API Response:', response);
          if (response.results.length > 0) {
            console.log('[VTO API] First Raw Item:', JSON.stringify(response.results[0], null, 2));
          }

          return {
            results: response.results.map(item => ({
              id: item.id,
              personImage: ((img) => {
                if (!img) return null;
                if (typeof img !== 'object') return { id: img };
                return {
                  id: img.id,
                  url: img.url || img.image?.url || img.ImageUrl || img.Image?.url || img.imageUrl,
                  removedBgImage: img.removedBgImage ? { url: img.removedBgImage.url } : (img.RemovedBgImage ? { url: img.RemovedBgImage.url } : null),
                  useRemovedImage: img.useRemovedImage !== undefined ? img.useRemovedImage : img.UseRemovedImage
                };
              })(item.personImage || item.PersonImage || item.person_image),
              resultImage: ((resImg) => { if (!resImg) return null; return Array.isArray(resImg) ? resImg.map(img => ({ id: img.id, url: img.url })) : { id: resImg.id, url: resImg.url }; })(item.resultImage || item.ResultImage || item.result_image),
              watermarkedImage: ((resImg) => { if (!resImg) return null; return Array.isArray(resImg) ? resImg.map(img => ({ id: img.id, url: img.url })) : { id: resImg.id, url: resImg.url }; })(item.watermarkedImage || item.WatermarkedImage || item.watermarked_image),
              removedBgImage: ((resImg) => { if (!resImg) return null; return Array.isArray(resImg) ? resImg.map(img => ({ id: img.id, url: img.url })) : { id: resImg.id, url: resImg.url }; })(item.removedBgImage || item.RemovedBgImage || item.removed_bg_image),
              useRemovedImage: item.useRemovedImage !== undefined ? item.useRemovedImage : true,
              thumbnailImage: ((resImg) => { if (!resImg) return null; return Array.isArray(resImg) ? resImg.map(img => ({ id: img.id, url: getThumbnailUrl(img.url) })) : { id: resImg.id, url: getThumbnailUrl(resImg.url) }; })(item.thumbnailImage || item.ThumbnailImage || item.thumbnail_image),
              closetClothes: Array.isArray(item.closetClothes)
                ? item.closetClothes.map(c => ({
                  id: c.id,
                  clothesName: c.clothesName,
                  image: c.image || c.coverImage || (c.source_product ? c.source_product.coverImage : null),
                  coverImage: c.coverImage || (c.source_product ? c.source_product.coverImage : null),
                  generatedProductPhoto: c.generatedProductPhoto || (c.source_product ? c.source_product.coverImage : null) || (c.source_listing && c.source_listing.images ? c.source_listing.images[0] : null),
                  licensingEnabled: c.source_listing?.licensingEnabled || c.source_product?.licensingEnabled || c.licensingEnabled || false,
                  licensingType: c.source_listing?.licensingType || c.source_product?.licensingType || c.licensingType || null,
                  licensingPrice: c.source_listing?.licensingPrice || c.source_product?.licensingPrice || c.licensingPrice || null,
                  currency: c.source_listing?.currency || c.source_product?.currency || c.currency || 'USD',
                  source_listing: c.source_listing,
                  source_product: c.source_product
                }))
                : item.closetClothes ? [{
                  id: item.closetClothes.id,
                  clothesName: item.closetClothes.clothesName,
                  image: item.closetClothes.image || item.closetClothes.coverImage || (item.closetClothes.source_product ? item.closetClothes.source_product.coverImage : null),
                  coverImage: item.closetClothes.coverImage || (item.closetClothes.source_product ? item.closetClothes.source_product.coverImage : null),
                  generatedProductPhoto: item.closetClothes.generatedProductPhoto || (item.closetClothes.source_product ? item.closetClothes.source_product.coverImage : null) || (item.closetClothes.source_listing && item.closetClothes.source_listing.images ? item.closetClothes.source_listing.images[0] : null),
                  licensingEnabled: item.closetClothes.source_listing?.licensingEnabled || item.closetClothes.source_product?.licensingEnabled || item.closetClothes.licensingEnabled || false,
                  licensingType: item.closetClothes.source_listing?.licensingType || item.closetClothes.source_product?.licensingType || item.closetClothes.licensingType || null,
                  licensingPrice: item.closetClothes.source_listing?.licensingPrice || item.closetClothes.source_product?.licensingPrice || item.closetClothes.licensingPrice || null,
                  currency: item.closetClothes.source_listing?.currency || item.closetClothes.source_product?.currency || item.closetClothes.currency || 'USD',
                  source_listing: item.closetClothes.source_listing,
                  source_product: item.closetClothes.source_product
                }] : [],
              garmentType: item.garmentType,
              garmentSource: item.garmentSource,
              isFavorite: item.isFavorite,
              confidence: item.confidence,
              garmentFit: item.garmentFit,
              processingTime: item.processingTime,
              apiCost: item.apiCost,
              viewCount: item.viewCount || 0,
              shareCount: item.shareCount || 0,
              tags: item.tags || [],
              generationMetadata: item.generationMetadata || item.GenerationMetadata || null,
              isPublic: item.isPublic || false,
              createdAt: item.created_at,
              updatedAt: item.updated_at
            })),
            pagination: response.pagination
          };
        }
        return { results: [], pagination: { total: 0, hasMore: false } };
      },
      transformErrorResponse: (response) => {
        console.error('VTO Results Query Error:', response);
        return {
          message: response?.data?.error?.message || 'Unable to load try-on results. Please try again.',
          code: response?.data?.error?.code || 'VTO_FETCH_ERROR',
          details: response?.data?.error?.details
        };
      }
    }),

    // Get specific VTO result by ID
    getVTOResultById: builder.query({
      query: (id) => ({
        url: API_ENDPOINTS.VTO_RESULT.BY_ID(id),
        method: 'GET'
      }),
      providesTags: (result, error, id) => [{ type: 'VTOResult', id }],
      transformResponse: (response) => {
        if (response && response.id) {
          return {
            id: response.id,
            personImage: ((img) => {
              if (!img) return null;
              if (typeof img !== 'object') return { id: img };
              return {
                id: img.id,
                url: img.url || img.image?.url || img.ImageUrl || img.Image?.url || img.imageUrl,
                removedBgImage: img.removedBgImage ? { url: img.removedBgImage.url } : (img.RemovedBgImage ? { url: img.RemovedBgImage.url } : null),
                useRemovedImage: img.useRemovedImage !== undefined ? img.useRemovedImage : img.UseRemovedImage
              };
            })(response.personImage || response.PersonImage || response.person_image),
            resultImage: ((resImg) => { if (!resImg) return null; return Array.isArray(resImg) ? resImg.map(img => ({ id: img.id, url: img.url })) : { id: resImg.id, url: resImg.url }; })(response.resultImage || response.ResultImage || response.result_image),
            watermarkedImage: ((resImg) => { if (!resImg) return null; return Array.isArray(resImg) ? resImg.map(img => ({ id: img.id, url: img.url })) : { id: resImg.id, url: resImg.url }; })(response.watermarkedImage || response.WatermarkedImage || response.watermarked_image),
            removedBgImage: ((resImg) => { if (!resImg) return null; return Array.isArray(resImg) ? resImg.map(img => ({ id: img.id, url: img.url })) : { id: resImg.id, url: resImg.url }; })(response.removedBgImage || response.RemovedBgImage || response.removed_bg_image),
            useRemovedImage: response.useRemovedImage !== undefined ? response.useRemovedImage : true,
            thumbnailImage: ((resImg) => { if (!resImg) return null; return Array.isArray(resImg) ? resImg.map(img => ({ id: img.id, url: getThumbnailUrl(img.url) })) : { id: resImg.id, url: getThumbnailUrl(resImg.url) }; })(response.thumbnailImage || response.ThumbnailImage || response.thumbnail_image),
              closetClothes: Array.isArray(response.closetClothes)
              ? response.closetClothes.map(c => ({
                id: c.id,
                clothesName: c.clothesName,
                image: c.image || c.coverImage || (c.source_product ? c.source_product.coverImage : null),
                coverImage: c.coverImage || (c.source_product ? c.source_product.coverImage : null),
                generatedProductPhoto: c.generatedProductPhoto || (c.source_product ? c.source_product.coverImage : null) || (c.source_listing && c.source_listing.images ? c.source_listing.images[0] : null),
                licensingEnabled: c.source_listing?.licensingEnabled || c.source_product?.licensingEnabled || c.licensingEnabled || false,
                licensingType: c.source_listing?.licensingType || c.source_product?.licensingType || c.licensingType || null,
                licensingPrice: c.source_listing?.licensingPrice || c.source_product?.licensingPrice || c.licensingPrice || null,
                currency: c.source_listing?.currency || c.source_product?.currency || c.currency || 'USD',
                source_listing: c.source_listing,
                source_product: c.source_product
              }))
              : response.closetClothes ? [{
                id: response.closetClothes.id,
                clothesName: response.closetClothes.clothesName,
                image: response.closetClothes.image || response.closetClothes.coverImage || (response.closetClothes.source_product ? response.closetClothes.source_product.coverImage : null),
                coverImage: response.closetClothes.coverImage || (response.closetClothes.source_product ? response.closetClothes.source_product.coverImage : null),
                generatedProductPhoto: response.closetClothes.generatedProductPhoto || (response.closetClothes.source_product ? response.closetClothes.source_product.coverImage : null) || (response.closetClothes.source_listing && response.closetClothes.source_listing.images ? response.closetClothes.source_listing.images[0] : null),
                licensingEnabled: response.closetClothes.source_listing?.licensingEnabled || response.closetClothes.source_product?.licensingEnabled || response.closetClothes.licensingEnabled || false,
                licensingType: response.closetClothes.source_listing?.licensingType || response.closetClothes.source_product?.licensingType || response.closetClothes.licensingType || null,
                licensingPrice: response.closetClothes.source_listing?.licensingPrice || response.closetClothes.source_product?.licensingPrice || response.closetClothes.licensingPrice || null,
                currency: response.closetClothes.source_listing?.currency || response.closetClothes.source_product?.currency || response.closetClothes.currency || 'USD',
                source_listing: response.closetClothes.source_listing,
                source_product: response.closetClothes.source_product
              }] : [],
            garmentType: response.garmentType,
            garmentSource: response.garmentSource,
            isFavorite: response.isFavorite,
            confidence: response.confidence,
            garmentFit: response.garmentFit,
            processingTime: response.processingTime,
            apiCost: response.apiCost,
            viewCount: response.viewCount || 0,
            shareCount: response.shareCount || 0,
            tags: response.tags || [],
            generationMetadata: response.generationMetadata || response.GenerationMetadata || null,
            isPublic: response.isPublic || false,
            createdAt: response.created_at,
            updatedAt: response.updated_at
          };
        }
        return null;
      }
    }),

    // Save VTO result with existing file IDs
    saveVTOResult: builder.mutation({
      query: ({
        personImageId,
        closetClothesId,
        resultImageId,
        thumbnailImageId,
        garmentType,
        garmentSource = 'user_upload',
        processingTime,
        apiCost,
        generationMetadata,
        confidence,
        garmentFit = 'good'
      }) => ({
        url: API_ENDPOINTS.VTO_RESULT.BASE,
        method: 'POST',
        body: {
          personImageId,
          closetClothesId,
          resultImageId,
          thumbnailImageId,
          garmentType,
          garmentSource,
          processingTime,
          apiCost,
          generationMetadata,
          confidence,
          garmentFit
        }
      }),
      invalidatesTags: ['VTOResult'],
      transformResponse: (response) => {
        if (response?.success && response?.data) {
          const item = response.data;
          return {
            id: item.id,
            personImage: ((img) => {
              if (!img) return null;
              if (typeof img !== 'object') return { id: img };
              return {
                id: img.id,
                url: img.url || img.image?.url || img.ImageUrl || img.Image?.url || img.imageUrl,
                removedBgImage: img.removedBgImage ? { url: img.removedBgImage.url } : (img.RemovedBgImage ? { url: img.RemovedBgImage.url } : null),
                useRemovedImage: img.useRemovedImage !== undefined ? img.useRemovedImage : img.UseRemovedImage
              };
            })(item.personImage || item.PersonImage || item.person_image),
            resultImage: ((resImg) => { if (!resImg) return null; return Array.isArray(resImg) ? resImg.map(img => ({ id: img.id, url: img.url })) : { id: resImg.id, url: resImg.url }; })(item.resultImage || item.ResultImage || item.result_image),
            thumbnailImage: ((resImg) => { if (!resImg) return null; return Array.isArray(resImg) ? resImg.map(img => ({ id: img.id, url: getThumbnailUrl(img.url) })) : { id: resImg.id, url: getThumbnailUrl(resImg.url) }; })(item.thumbnailImage || item.ThumbnailImage || item.thumbnail_image),
            closetClothes: Array.isArray(item.closetClothes)
              ? item.closetClothes.map(c => ({
                id: c.id,
                clothesName: c.clothesName,
                image: c.image
              }))
              : item.closetClothes ? [{
                id: item.closetClothes.id,
                clothesName: item.closetClothes.clothesName,
                image: item.closetClothes.image
              }] : [],
            garmentType: item.garmentType,
            garmentSource: item.garmentSource,
            isFavorite: item.isFavorite,
            confidence: item.confidence,
            garmentFit: item.garmentFit,
            processingTime: item.processingTime,
            apiCost: item.apiCost,
            generationMetadata: item.generationMetadata || item.GenerationMetadata || null,
            createdAt: item.created_at,
            updatedAt: item.updated_at
          };
        }
        throw new Error(response?.message || 'Save failed');
      }
    }),

    // Save VTO result with file uploads
    saveVTOResultWithUploads: builder.mutation({
      query: ({
        personImageFile,
        resultImageFile,
        thumbnailImageFile,
        closetClothesId,
        garmentType,
        garmentSource = 'user_upload',
        processingTime,
        apiCost,
        generationMetadata,
        confidence,
        garmentFit = 'good'
      }) => {
        const formData = new FormData();

        if (personImageFile) {
          formData.append('personImage', personImageFile);
        }
        if (resultImageFile) {
          formData.append('resultImage', resultImageFile);
        }
        if (thumbnailImageFile) {
          formData.append('thumbnailImage', thumbnailImageFile);
        }
        if (closetClothesId) {
          formData.append('closetClothesId', closetClothesId);
        }

        formData.append('garmentType', garmentType);
        formData.append('garmentSource', garmentSource);

        if (processingTime) {
          formData.append('processingTime', processingTime.toString());
        }
        if (apiCost) {
          formData.append('apiCost', apiCost.toString());
        }
        if (generationMetadata) {
          formData.append('generationMetadata', JSON.stringify(generationMetadata));
        }
        if (confidence) {
          formData.append('confidence', confidence.toString());
        }
        formData.append('garmentFit', garmentFit);

        return {
          url: API_ENDPOINTS.VTO_RESULT.UPLOAD,
          method: 'POST',
          body: formData
        };
      },
      invalidatesTags: ['VTOResult'],
      transformResponse: (response) => {
        if (response?.success && response?.data) {
          const item = response.data;
          return {
            id: item.id,
            personImage: ((img) => {
              if (!img) return null;
              if (typeof img !== 'object') return { id: img };
              return {
                id: img.id,
                url: img.url || img.image?.url || img.ImageUrl || img.Image?.url || img.imageUrl,
                removedBgImage: img.removedBgImage ? { url: img.removedBgImage.url } : (img.RemovedBgImage ? { url: img.RemovedBgImage.url } : null),
                useRemovedImage: img.useRemovedImage !== undefined ? img.useRemovedImage : img.UseRemovedImage
              };
            })(item.personImage || item.PersonImage || item.person_image),
            resultImage: ((resImg) => { if (!resImg) return null; return Array.isArray(resImg) ? resImg.map(img => ({ id: img.id, url: img.url })) : { id: resImg.id, url: resImg.url }; })(item.resultImage || item.ResultImage || item.result_image),
            thumbnailImage: ((resImg) => { if (!resImg) return null; return Array.isArray(resImg) ? resImg.map(img => ({ id: img.id, url: getThumbnailUrl(img.url) })) : { id: resImg.id, url: getThumbnailUrl(resImg.url) }; })(item.thumbnailImage || item.ThumbnailImage || item.thumbnail_image),
            closetClothes: Array.isArray(item.closetClothes)
              ? item.closetClothes.map(c => ({
                id: c.id,
                clothesName: c.clothesName,
                image: c.image
              }))
              : item.closetClothes ? [{
                id: item.closetClothes.id,
                clothesName: item.closetClothes.clothesName,
                image: item.closetClothes.image
              }] : [],
            garmentType: item.garmentType,
            garmentSource: item.garmentSource,
            isFavorite: item.isFavorite,
            confidence: item.confidence,
            garmentFit: item.garmentFit,
            processingTime: item.processingTime,
            apiCost: item.apiCost,
            generationMetadata: item.generationMetadata || item.GenerationMetadata || null,
            createdAt: item.created_at,
            updatedAt: item.updated_at
          };
        }
        throw new Error(response?.message || 'Upload failed');
      }
    }),

    // Update VTO result (favorite, tags, etc.)
    updateVTOResult: builder.mutation({
      query: ({ id, ...updates }) => ({
        url: API_ENDPOINTS.VTO_RESULT.UPDATE(id),
        method: 'PUT',
        body: updates
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'VTOResult', id },
        'VTOResult'
      ]
    }),

    // Favorite/unfavorite VTO result
    favoriteVTOResult: builder.mutation({
      query: ({ id, favorite }) => ({
        url: API_ENDPOINTS.VTO_RESULT.UPDATE(id),
        method: 'PUT',
        body: { isFavorite: favorite }
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'VTOResult', id },
        'VTOResult'
      ]
    }),

    // Delete VTO result
    deleteVTOResult: builder.mutation({
      query: (id) => ({
        url: API_ENDPOINTS.VTO_RESULT.DELETE(id),
        method: 'DELETE'
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'VTOResult', id },
        'VTOResult'
      ]
    }),

    // Get user VTO statistics
    getVTOStats: builder.query({
      query: () => ({
        url: API_ENDPOINTS.VTO_RESULT.STATS,
        method: 'GET'
      }),
      transformResponse: (response) => {
        return {
          totalResults: response.totalResults || 0,
          favoriteResults: response.favoriteResults || 0,
          resultsByType: response.resultsByType || [],
          totalCost: response.totalCost || 0
        };
      }
    }),

    // Get VTO result by cache key (for legacy support)
    getVTOResultByCacheKey: builder.query({
      query: (cacheKey) => ({
        url: API_ENDPOINTS.VTO_RESULT.BY_CACHE_KEY(cacheKey),
        method: 'GET'
      }),
      transformResponse: (response) => {
        if (response && response.id) {
          return {
            id: response.id,
            personImage: ((img) => {
              if (!img) return null;
              if (typeof img !== 'object') return { id: img };
              return {
                id: img.id,
                url: img.url || img.image?.url || img.ImageUrl || img.Image?.url || img.imageUrl,
                removedBgImage: img.removedBgImage ? { url: img.removedBgImage.url } : (img.RemovedBgImage ? { url: img.RemovedBgImage.url } : null),
                useRemovedImage: img.useRemovedImage !== undefined ? img.useRemovedImage : img.UseRemovedImage
              };
            })(response.personImage || response.PersonImage || response.person_image),
            resultImage: ((resImg) => { if (!resImg) return null; return Array.isArray(resImg) ? resImg.map(img => ({ id: img.id, url: img.url })) : { id: resImg.id, url: resImg.url }; })(response.resultImage || response.ResultImage || response.result_image),
            thumbnailImage: ((resImg) => { if (!resImg) return null; return Array.isArray(resImg) ? resImg.map(img => ({ id: img.id, url: getThumbnailUrl(img.url) })) : { id: resImg.id, url: getThumbnailUrl(resImg.url) }; })(response.thumbnailImage || response.ThumbnailImage || response.thumbnail_image),
            closetClothes: Array.isArray(response.closetClothes)
              ? response.closetClothes.map(c => ({
                id: c.id,
                clothesName: c.clothesName,
                image: c.image
              }))
              : response.closetClothes ? [{
                id: response.closetClothes.id,
                clothesName: response.closetClothes.clothesName,
                image: response.closetClothes.image
              }] : [],
            garmentType: response.garmentType,
            garmentSource: response.garmentSource,
            isFavorite: response.isFavorite,
            confidence: response.confidence,
            garmentFit: response.garmentFit,
            processingTime: response.processingTime,
            apiCost: response.apiCost,
            generationMetadata: response.generationMetadata || response.GenerationMetadata || null,
            createdAt: response.created_at,
            updatedAt: response.updated_at
          };
        }
        return null;
      }
    }),

    // Remove background from VTO result
    removeVTOResultBackground: builder.mutation({
      query: (id) => ({
        url: `/virtual-try-on-results/${id}/remove-background`,
        method: 'POST'
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'VTOResult', id },
        'VTOResult'
      ]
    }),

    // Toggle background for VTO result
    toggleVTOResultBackground: builder.mutation({
      query: (id) => ({
        url: `/virtual-try-on-results/${id}/toggle-background`,
        method: 'PUT'
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'VTOResult', id },
        'VTOResult'
      ]
    }),
    // Toggle isPublic on a VTO result (for SUSU Map sharing)
    togglePublishVTOResult: builder.mutation({
      query: ({ id, isPublic }) => ({
        url: API_ENDPOINTS.VTO_RESULT.UPDATE(id),
        method: 'PUT',
        body: { isPublic }
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'VTOResult', id },
        'VTOResult'
      ]
    })
  })
});

// Export hooks for the VTO result API
export const {
  useGetUserVTOResultsQuery,
  useGetVTOResultByIdQuery,
  useSaveVTOResultMutation,
  useSaveVTOResultWithUploadsMutation,
  useUpdateVTOResultMutation,
  useFavoriteVTOResultMutation,
  useDeleteVTOResultMutation,
  useGetVTOStatsQuery,
  useGetVTOResultByCacheKeyQuery,
  useRemoveVTOResultBackgroundMutation,
  useToggleVTOResultBackgroundMutation,
  useTogglePublishVTOResultMutation
} = enhancedVTOResultApi;

export default enhancedVTOResultApi;